import { useState, useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import { message } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { cloneDeep } from 'lodash';
import { tagStore } from '@laigu/store';

import { getConvData, combineConvData, addConvStatus, getAllStreams, addRemindMessage } from './selector';

import {
  getConversationStreamApi,
  getConvSumConvIdsApi,
  getConversationSummaryApi,
  createConvSummaryApi,
  updateConvSummaryApi,
  handleConvTagApi,
  summaryGptApi
} from './api/index';
import { useDispatch } from '../../../store/react-redux';
import { setAdvertise } from '../../../store/reducers/conversation';
import useTranslateMessage from '../use-translate-message';
import useSpeechToTxt from '../use-speech-to-txt';

const nowString = moment().format('YYYY-MM-DDTHH:mm:ss.SSSSSS');

const useConvSummary = props => {
  const { trackId } = props;
  const [convContent, setConvContent] = useState({});
  const [summaryList, setSummaryList] = useState({
    convIds: [],
    fetchTrackId: false
  });

  const getConvSumConvIds = useCallback(trackId => {
    return getConvSumConvIdsApi({ trackId }).then(req => {
      setSummaryList({
        fetchTrackId: trackId,
        convIds: req
      });
      return req;
    });
  }, []);

  const getConversationSummary = useCallback(convId => {
    getConversationSummaryApi(convId).then(res => {
      setConvContent(convContent => {
        return {
          ...convContent,
          [convId]: res
        };
      });
    });
  }, []);

  const getConvSummaryContent = useCallback(
    convId => {
      const { fetchTrackId, convIds } = summaryList;
      if (fetchTrackId !== trackId) {
        getConvSumConvIds(trackId).then(convIds => {
          if (convIds && convIds.includes(convId)) {
            getConversationSummary(convId);
          }
        });
      } else if (convIds.includes(convId)) {
        getConversationSummary(convId);
      }
    },
    [summaryList, trackId]
  );

  const createConvSummary = useCallback(({ content, convId, successCb }) => {
    return createConvSummaryApi({ convId, content }).then(() => {
      message.success('对话小结已创建！');
      if (successCb) {
        successCb();
      }
      getConversationSummary(convId);
    });
  }, []);

  const updateConvSummary = useCallback(({ content, convId, successCb }) => {
    return updateConvSummaryApi({ convId, content }).then(() => {
      message.success('对话小结已更新！');
      if (successCb) {
        successCb();
      }
      getConversationSummary(convId);
    });
  }, []);

  useEffect(() => {
    setSummaryList({
      convIds: [],
      fetchTrackId: null
    });
  }, [trackId]);

  return {
    convSummary: convContent,
    getConvSummaryContent,
    createConvSummary,
    updateConvSummary
  };
};

const useConvTag = props => {
  const { trackId } = props;
  const [convTag, setConvTag] = useState({});

  const handleSetConvTag = useCallback(({ convId, tags }) => {
    setConvTag(convTag => ({
      ...convTag,
      [convId]: tags
    }));
  }, []);

  const handleChangeConvTag = useCallback((tags, convId) => {
    return handleConvTagApi({ convId, tags }).then(() => {
      setConvTag(convTag => {
        return {
          ...convTag,
          [convId]: tags
        };
      });
    });
  }, []);

  useEffect(() => {
    setConvTag({});
  }, [trackId]);

  return {
    convTag,
    handleSetConvTag,
    handleChangeConvTag
  };
};

const useHistoryConvs = props => {
  const {
    trackId,
    currentConvId,
    agentList,
    customerField,
    extraConv = [],
    loadImmediately,
    meId,
    translateLanguage,
    AutomaticTranslation,
    AutoTranslateStatus,
    lastedConvId
  } = props;
  const { convTagList } = tagStore.useConvTag();
  const [, setGptSummaryMsgIds] = useLocalStorageState('gptSummaryMsgIds', []);
  const { convSummary, getConvSummaryContent, createConvSummary, updateConvSummary } = useConvSummary({ trackId });
  const { convTag, handleSetConvTag, handleChangeConvTag } = useConvTag({ trackId });

  const [convStream, setConvStream] = useState([]);
  const [reqestingConv, setReqestingConv] = useState({});
  const dispatch = useDispatch();
  const [convLoading, setConvLoading] = useState(false);

  const appendStream = convData => {
    setConvStream(convStream => {
      return [...convStream, convData];
    });
  };

  const translateProps = useMemo(
    () => ({
      trackId,
      meId,
      translateLanguage,
      AutomaticTranslation,
      isOtherPage: !!AutoTranslateStatus
    }),
    [trackId, meId, translateLanguage, AutomaticTranslation, AutoTranslateStatus]
  );
  const {
    currentTranslateData,
    handleTranslateMessage,
    handleAutoTranslate,
    setAgentTranslateData
  } = useTranslateMessage(translateProps);
  const { currentSpeechData, handleSpeechToTxt } = useSpeechToTxt({ trackId, currentConvId });

  const handleAutoTranslateStreams = useCallback(
    streams => {
      const autoTranslationAvailable = AutoTranslateStatus();
      if (autoTranslationAvailable && streams.length) {
        handleAutoTranslate(streams);
      }
    },
    [AutoTranslateStatus, handleAutoTranslate]
  );

  /*
   * 由于对话流流里面的visit_info 的 first_visit_page_source_keyword_by_session
   * 这个字段是创建对话当时init_conv事件发出时的，有些对话这个时间还没有值
   * 有后来再从百度补拉的，这个事件是历史事件，后端也没法更新
   * 所以需要判断 如果有的话直接用 没有的话
   * 需要去最外层的conv_info	里面的 first_visit_page_source_keyword_by_session
   */
  const getStream = useCallback(
    async (trackId, convId) => {
      setConvLoading(true);
      setReqestingConv(reqestingConv => ({ ...reqestingConv, [convId]: true }));
      const {
        streams = [],
        tag_ids,
        ended_on,
        before_conv,
        after_conv,
        advertise,
        conv_info = {}
      } = await getConversationStreamApi({
        trackId,
        convId
      });

      const innerConvStream = cloneDeep(streams).map(stream => {
        if (stream?.action === 'init_conv') {
          try {
            if (!stream?.body?.visit_info) {
              stream.body.visit_info = {};
            }
            const { first_visit_page_source_keyword_by_session = '' } = stream?.body?.visit_info;
            if (!first_visit_page_source_keyword_by_session) {
              stream.body.visit_info.first_visit_page_source_keyword_by_session =
                conv_info?.first_visit_page_source_keyword_by_session ?? '';
            }
          } catch (error) {
            console.error(error);
          }

          return stream;
        }

        return stream;
      });

      dispatch(setAdvertise(advertise));
      setReqestingConv(reqestingConv => ({ ...reqestingConv, [convId]: false }));
      appendStream({
        convId,
        tag_ids,
        ended_on: (ended_on && ended_on[0]) || nowString,
        streams: innerConvStream,
        before_conv,
        after_conv
      });
      handleSetConvTag({ convId, tags: tag_ids });

      if (currentConvId === convId) {
        if (streams?.length && AutoTranslateStatus && AutomaticTranslation) {
          handleAutoTranslateStreams(streams);
        }
      }
      setConvLoading(false);
    },
    [currentConvId, handleAutoTranslateStreams]
  );

  const clearStream = () => {
    setConvStream([]);
    setReqestingConv({});
  };

  const handleLoadMoreStream = useCallback(({ convId }) => getStream(trackId, convId), [trackId, getStream]);

  const handleGptSummary = useCallback((convId, eventId, eventTime) => {
    setGptSummaryMsgIds(gptSummaryMsgIds => [...gptSummaryMsgIds, eventId]);

    return summaryGptApi(convId, eventId, eventTime)
      .then(() => {
        return 1;
      })
      .catch(() => {
        setGptSummaryMsgIds(gptSummaryMsgIds => gptSummaryMsgIds.filter(id => id != eventId));
        return 0;
      });
  });

  useEffect(() => {
    clearStream();
    if (loadImmediately) {
      if (trackId && currentConvId) {
        getStream(trackId, currentConvId);
        getConvSummaryContent(currentConvId);
      }
    }
  }, [currentConvId, trackId]);

  useEffect(() => {
    getConvSummaryContent(lastedConvId);
  }, [lastedConvId]);

  const convWithExtra = useMemo(() => {
    return [...convStream, ...extraConv];
  }, [extraConv, convStream]);
  const parsedConvStream = getConvData(convWithExtra);
  const combineResult = combineConvData(
    parsedConvStream,
    convSummary,
    agentList,
    convTagList,
    customerField,
    convTag,
    currentTranslateData,
    currentSpeechData
  );
  const resultConvStatus = addConvStatus(reqestingConv, combineResult);

  // 添加引用消息内容到extra
  const resultRemindMessage = addRemindMessage(resultConvStatus);
  const allStreams = getAllStreams(resultRemindMessage);

  return {
    data: resultRemindMessage,
    allStreams,
    reqestingConv,
    handleLoadMoreStream,
    getConvSummaryContent,
    createConvSummary,
    updateConvSummary,
    handleChangeConvTag,
    handleTranslateMessage,
    handleAutoTranslate,
    handleSpeechToTxt,
    handleGptSummary,
    convLoading,
    setAgentTranslateData,
    handleSetConvTag
  };
};

export default useHistoryConvs;
